<template>
  <div class="container profile-detail-block event-block">
    <div
      class="event-card"
      :class="{ clickable: clickable }"
      @click="handleClick"
    >
      <!-- Date/Time Box -->
      <div class="event-date-box">
        <span class="event-date-day">{{ formatDateForBox(event.event_date.from) }}</span>
        <span class="event-date-time-range">{{ formatTimeForBox(event.event_date.from, event.event_date.to) }}</span>
      </div>

      <!-- Area A: Participating Company Names -->
      <div class="event-area-a">
        <div class="event-companies">
          <span
            v-for="company in event.companies"
            :key="company._id"
            class="company-badge"
          >{{ company.name }}</span>
        </div>
      </div>

      <!-- Area B: Event Title -->
      <div class="event-area-b">
        <p class="event-name">{{ event.event_name }}</p>
      </div>

      <!-- Right/Bottom: Application Deadline + Application Status -->
      <div class="event-right-side">
        <span class="event-deadline-text">
          <i class="fa fa-clock-o" aria-hidden="true"></i>
          <span
            v-if="useMemoize"
            v-memoize:formatApplicationDeadline="[event.application_period.to]"
          ></span>
          <span v-else>{{ formatApplicationDeadline(event.application_period.to) }}</span>
        </span>
        <span
          :class="getApplicationButtonClass(event)"
          class="application-status-badge"
          @click.stop
        >{{ getApplicationButtonText(event) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import eventHelpers from '../mixins/eventHelpers';

export default {
  name: 'EventCard',
  mixins: [eventHelpers],
  props: {
    event: {
      type: Object,
      required: true,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    useMemoize: {
      type: Boolean,
      default: false,
    },
    showMobileLayout: {
      type: Boolean,
      default: true,
    },
  },
  directives: {
    memoize: {
      bind(el, binding, vnode) {
        const cache = new Map();
        const key = JSON.stringify(binding.value);
        if (!cache.has(key)) {
          cache.set(key, vnode.context[binding.arg](...binding.value));
        }
        el.textContent = cache.get(key);
      },
    },
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
    }),
  },
  methods: {
    handleClick() {
      if (this.clickable && this.$listeners.click) {
        this.$emit('click', this.event._id);
      }
    },
    handleButtonClick() {
      if (this.$listeners.buttonClick) {
        this.$emit('buttonClick', this.event);
      } else if (this.clickable) {
        this.handleClick();
      }
    },
    formatDateForBox(date) {
      return moment(date).locale('ja').utc().format('M/D(ddd)');
    },
    formatTimeForBox(from, to) {
      const fromTime = moment(from).utc().format('HH:mm');
      const toTime = moment(to).utc().format('HH:mm');
      return `${fromTime} - ${toTime}`;
    },
  },
};
</script>

<style scoped>
.event-block {
  position: relative;
  padding: 15px 25px !important;
  margin-bottom: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #ffffff !important;
}

/*
 * Desktop layout (3-column grid, date box spans both rows):
 *   [ date | companies | right ]
 *   [ date |   title   | right ]
 */
.event-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: 1fr 1fr;
  grid-template-areas:
    "date companies right"
    "date title     right";
  column-gap: 16px;
  row-gap: 4px;
  border: 0;
  min-height: 90px;
}

.event-card.clickable {
  cursor: pointer;
}

/* Date/Time Box */
.event-date-box {
  grid-area: date;
  background-color: #006C6B;
  color: #ffffff;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  text-align: center;
  gap: 4px;
  min-width: 88px;
}

.event-date-day {
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  line-height: 1.3;
}

.event-date-time-range {
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  line-height: 1.3;
}

/* Area A: Company Names */
.event-area-a {
  grid-area: companies;
  display: flex;
  align-items: center;
}

.event-companies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.company-badge {
  background-color: #E5E7EB;
  color: #000000;
  padding: 5px 14px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.4;
}

/* Area B: Event Title */
.event-area-b {
  grid-area: title;
  display: flex;
  align-items: center;
  padding-top: 4px;
}

.event-name {
  font-weight: 400;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
  color: #000000;
}

/* Right Side: Deadline + Status (desktop — stacked vertically) */
.event-right-side {
  grid-area: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
  min-width: 110px;
}

.event-deadline-text {
  font-size: 13px;
  font-weight: 400;
  color: #DD3847;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.event-deadline-text .fa {
  font-size: 13px;
  flex-shrink: 0;
}

.application-status-badge {
  border-radius: 20px;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  border: none;
  width: 168px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  line-height: 1;
  background-color: #E5E7EB;
  cursor: default;
  user-select: none;
  flex-shrink: 0;
}

.applied-status {
  color: #5245CC;
}

.not-applied-status {
  color: #555555;
}

/*
 * Mobile layout (2-column grid, 3 rows):
 *   [ date | companies ]
 *   [ title (full)     ]
 *   [ deadline+status  ]
 */
@media (max-width: 768px) {
  .event-block {
    padding: 12px 15px !important;
  }

  .event-card {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto auto;
    grid-template-areas:
      "date companies"
      "title title"
      "bottom bottom";
    column-gap: 12px;
    row-gap: 8px;
    min-height: unset;
  }

  .event-date-box {
    padding: 10px 14px;
    min-width: 76px;
    border-radius: 8px;
  }

  .event-date-day {
    font-size: 13px;
  }

  .event-date-time-range {
    font-size: 11px;
  }

  .event-area-a {
    align-items: flex-start;
    padding-top: 2px;
  }

  .event-companies {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .company-badge {
    font-size: 13px;
    padding: 4px 12px;
    border-radius: 12px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
  }

  .event-area-b {
    padding-top: 6px;
  }

  .event-name {
    font-size: 13px;
  }

  /* Bottom row: deadline on left, status badge on right */
  .event-right-side {
    grid-area: bottom;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    min-width: unset;
    gap: 8px;
  }

  .application-status-badge {
    font-size: 12px;
    width: 168px;
    height: 25px;
    padding: 0;
  }
}

@media (max-width: 530px) {
  .event-block {
    padding: 10px !important;
  }

  .event-date-box {
    padding: 8px 12px;
    min-width: 68px;
    border-radius: 7px;
  }

  .event-date-day {
    font-size: 12px;
  }

  .event-date-time-range {
    font-size: 10px;
  }

  .company-badge {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 10px;
  }

  .event-name {
    font-size: 12px;
  }

  .event-deadline-text {
    font-size: 12px;
  }

  .application-status-badge {
    font-size: 11px;
    width: 168px;
    height: 25px;
    padding: 0;
  }
}
</style>
